<template>
  <div class="sidebar-layout">
    <t-menu
      width="190px"
      class="side-nav"
      :value="active"
      :expanded="expanded"
      @expand="onExpand"
    >
      <template v-for="item in menuList">
        <t-submenu
          v-if="item.children"
          :key="item.name"
          :name="item.name"
          :value="item.name"
          :title="item.title"
        >
          <template #icon>
            <component :is="item.icon" />
          </template>
          <t-menu-item
            v-for="child in item.children"
            :key="child.path"
            :to="child.path"
            :name="child.name"
            :value="child.path"
          >
            {{ child.title }}
          </t-menu-item>
        </t-submenu>
        <t-menu-item
          v-else
          :key="item.path"
          :to="item.path"
          :name="item.name"
          :value="item.path"
        >
          <template #icon>
            <component :is="item.icon" />
          </template>
          {{ item.title }}
        </t-menu-item>
      </template>
    </t-menu>
    <div v-if="yearProjects.length" class="year-block">
      <div class="year-label">本年度捐赠</div>
      <button
        v-for="(item, index) in yearProjects"
        :key="item.cp_code || `${item.project_name}-${index}`"
        type="button"
        class="year-item"
        :class="{ 'is-active': isYearProjectActive(item) }"
        @click="goYearProject(item)"
      >
        {{ item.project_name }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {
  HeartIcon,
  CalendarIcon,
  ChartIcon,
  SettingIcon,
  DownloadIcon,
} from 'tdesign-icons-vue';
import { getYearDonationList, getDonationPresence } from '@/api/donation';
import { DONATION_DETAIL_PATH } from '@/views/donation/constants';
import { unwrapPayload } from '@/views/donation/format';
import {
  persistEntInitSession,
  persistHasDonation,
  readHasActivity,
  readHasDonation,
} from '@/utils/entStatus';
import { $getEntInitInfo } from '@/utils/api';

export default Vue.extend({
  name: 'sideNav',
  components: {
    HeartIcon,
    CalendarIcon,
    ChartIcon,
    SettingIcon,
    DownloadIcon,
  },
  props: {
    menu: Array,
    layout: String,
  },
  data() {
    return {
      expanded: [],
      hasActivity: readHasActivity(),
      hasDonation: readHasDonation(),
      yearProjects: [],
      allMenuList: [{
        path: '/donation',
        name: 'donation',
        title: '捐赠管理',
        icon: 'HeartIcon',
        requireDonation: true,
      }, {
        path: '/actList',
        name: 'actList',
        title: '活动管理',
        icon: 'CalendarIcon',
        requireActivity: true,
      }, {
        path: '/report',
        name: 'report',
        title: '向善记录',
        icon: 'ChartIcon',
      }, {
        path: '/entInfo',
        name: 'settings',
        title: '基础设置',
        icon: 'SettingIcon',
      }, {
        path: '/exportList',
        name: 'exportList',
        title: '数据导出任务列表',
        icon: 'DownloadIcon',
        requireActivity: true,
      }],
    };
  },
  computed: {
    menuList() {
      return this.allMenuList.filter(item => {
        if (item.requireActivity && !this.hasActivity) return false;
        if (item.requireDonation && !this.hasDonation) return false;
        return true;
      });
    },
    leafPaths() {
      return this.menuList.reduce((acc, item) => {
        if (item.children) return acc.concat(item.children.map(child => child.path));
        return acc.concat(item.path);
      }, []);
    },
    currentDetailId() {
      if (this.$route.path !== DONATION_DETAIL_PATH) return '';
      return String(this.$route.query.id || this.$route.query.cp_code || '');
    },
    yearProjectIds() {
      const ids = {};
      (this.yearProjects || []).forEach(item => {
        if (item.cp_code) ids[item.cp_code] = true;
      });
      return ids;
    },
    active() {
      const path = this.$route.path;
      if (path.startsWith('/entInfo') || path.startsWith('/member')) return '/entInfo';
      // 本年捐赠详情由下方项目名高亮，不再点亮「捐赠管理」
      if (path === DONATION_DETAIL_PATH && this.currentDetailId && this.yearProjectIds[this.currentDetailId]) {
        return '';
      }
      if (path.startsWith('/donation')) return '/donation';
      if (path === '/act/add') return '/actList';
      const sortedPaths = this.leafPaths.slice().sort((a, b) => b.length - a.length);
      return sortedPaths.find(item => path.startsWith(item)) || '';
    },
  },
  watch: {
    $route() {
      this.syncFlags();
    },
  },
  created() {
    this.syncFlags();
    this.loadDonationMeta();
  },
  methods: {
    onExpand(value) {
      this.expanded = value;
    },
    syncFlags() {
      this.hasActivity = readHasActivity();
      this.hasDonation = readHasDonation();
      // sessionStorage 中没有 ent_has_activity 记录时（如入驻流程跳转后），主动拉取一次
      if (sessionStorage.getItem('ent_has_activity') === null) {
        $getEntInitInfo().then(res => {
          persistEntInitSession(res);
          this.hasActivity = readHasActivity();
        }).catch(() => {});
      }
    },
    loadDonationMeta() {
      getDonationPresence()
        .then(res => {
          const data = unwrapPayload(res);
          const total = Number(data.total || data.Total || 0);
          this.hasDonation = total > 0 || !!(data.list || data.items || []).length;
        })
        .catch(() => {
          this.hasDonation = false;
          persistHasDonation({ total: 0 });
        });
      this.loadYearProjects();
    },
    loadYearProjects() {
      const year = new Date().getFullYear();
      getYearDonationList()
        .then(res => {
          const list = unwrapPayload(res).list || unwrapPayload(res).items || [];
          const yearPrefix = String(year);
          const inYear = list.filter(item => {
            const date = String(item.donation_date || item.donationDate || '').trim();
            return date.indexOf(yearPrefix) === 0;
          });
          this.yearProjects = inYear.map(item => ({
            project_name: String(item.project_name || item.projectName || '').trim(),
            cp_code: this.normalizeCpCode(item),
          })).filter(item => item.cp_code);
        })
        .catch(() => {
          this.yearProjects = [];
        });
    },
    normalizeCpCode(item) {
      if (!item) return '';
      const raw = item.cp_code != null && item.cp_code !== '' ? item.cp_code : item.cpCode;
      if (raw == null || raw === '') return '';
      return String(raw);
    },
    isYearProjectActive(item) {
      const code = this.normalizeCpCode(item);
      return !!code && this.currentDetailId === code;
    },
    goYearProject(item) {
      const code = this.normalizeCpCode(item);
      if (!code) return;
      if (this.$route.path === DONATION_DETAIL_PATH && this.currentDetailId === code) return;
      this.$router.push({
        path: DONATION_DETAIL_PATH,
        query: { id: code },
      });
    },
  },
});
</script>

<style lang="less" scoped>
.sidebar-layout {
  height: 100%;
  overflow-y: auto;
  background: #fff;
}

.side-nav {
  height: auto !important;

  /deep/ .t-icon + .t-menu__content,
  /deep/ .t-icon + .t-menu__item-link {
    margin-left: 0;
  }
}

.year-block {
  position: relative;
  margin-top: 8px;
  padding-bottom: 16px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 16px;
    left: 16px;
    height: 1px;
    background: #e7e7e7;
  }
}

.year-label,
.year-item {
  /* 与上方菜单 icon 对齐：t-menu 内边距 + item padding-left */
  padding-left: calc(var(--td-comp-paddingLR-s, 8px) + 16px);
  padding-right: 16px;
  box-sizing: border-box;
}

.year-label {
  padding-top: 12px;
  padding-bottom: 4px;
  font-size: 12px;
  line-height: 20px;
  color: rgba(0, 0, 0, 0.4);
}

.year-item {
  display: block;
  width: 100%;
  padding-top: 8px;
  padding-bottom: 8px;
  overflow: hidden;
  border: none;
  background: transparent;
  font-size: 14px;
  line-height: 22px;
  color: rgba(0, 0, 0, 0.9);
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: var(--td-brand-color);
  }

  &.is-active {
    color: var(--td-brand-color);
    background-color: var(--td-brand-color-1);
  }
}
</style>
